/**
 * Device ID utility for identifying devices across the app.
 * Uses Capacitor Device.getId() for native platforms, falls back to localStorage UUID for web.
 */
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Preferences } from "@capacitor/preferences";

const STORAGE_KEY = "phone_id";

/**
 * Get or create a persistent device identifier.
 * On native platforms, uses Capacitor Device.getId().
 * On web, generates and stores a UUID in localStorage.
 */
export async function getStoredPhoneId(): Promise<string> {
  // On native platforms, use Capacitor Device API
  if (Capacitor.isNativePlatform()) {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (value) return value;

      const { identifier } = await Device.getId();
      await Preferences.set({ key: STORAGE_KEY, value: identifier });
      return identifier;
    } catch (error) {
      console.warn("Failed to get device ID from Capacitor, falling back to localStorage", error);
      // Fall through to web fallback
    }
  }

  // Web fallback: use localStorage with UUID
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;

  // Generate a UUID v4
  const uuid = generateUUID();
  localStorage.setItem(STORAGE_KEY, uuid);
  return uuid;
}

/**
 * Generate a UUID v4 for web fallback
 */
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
